import type { NodeViewProps } from "@tiptap/react";
import { useCallback, useEffect, useId } from "react";
import { DATA_KEY_ATTR } from "./constants";
import { DynamicChipNodeView } from "./dynamic-chip-node";
import { useDynamicNodeContext } from "./dynamic-node-context";
import {
  consumeEditorOpen,
  getSlashDataKeyItems,
} from "./slash-command/data-key-registry";

/**
 * Data-key node: an inline chip resolving a `{{ data-key }}` reference against
 * the document's answers (its own version first, then the most-recent finalized
 * revision within the document's device scope — its own device plus org-level
 * documents). The chip/popover/keyboard behaviour lives in the shared
 * `DynamicChipNodeView`; this wraps it with the data-key resolver + picker.
 */
export const DataKeyNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  deleteNode,
  editor,
  getPos,
}) => {
  const dataKey = (node.attrs[DATA_KEY_ATTR] as string) ?? "";
  const { resolveDataKey, frozen, registerKeys, unregisterKeys, isKeyPending } =
    useDynamicNodeContext();
  const nodeId = useId();

  // Tell the provider which key this node needs so the backend is asked for it.
  useEffect(() => {
    registerKeys(nodeId, dataKey ? [dataKey] : []);
    return () => unregisterKeys(nodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const selectNode = useCallback(() => {
    const position = getPos();
    if (typeof position === "number") {
      editor.chain().focus().setNodeSelection(position).run();
    }
  }, [editor, getPos]);

  return (
    <DynamicChipNodeView
      node={node}
      updateAttributes={updateAttributes}
      deleteNode={deleteNode}
      onSelect={selectNode}
      config={{
        attr: DATA_KEY_ATTR,
        resolution: dataKey ? resolveDataKey(dataKey) : null,
        getItems: getSlashDataKeyItems,
        consumePendingOpen: () => consumeEditorOpen("datakey"),
        loading: dataKey ? isKeyPending(dataKey) : false,
        frozen,
        interactive: editor.isEditable,
        headerLabel: "Data key",
        emptyLabel: "Select data key",
        searchPlaceholder: "Search data keys…",
        noMatchLabel: "No matching data keys",
        info: {
          legend: true,
          note: "Data key values can only be resolved from other documents within the same device or from organization-level documents (without a device). Organization-level documents can only access other organization data keys.",
        },
      }}
    />
  );
};

export default DataKeyNodeView;
